import { useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { AdminPageHeader, DeleteModal, Form, AdminFormAction } from '@ui';
import { HospitalContactForm, hospitalContactFormSchema } from '@lp/features/admin/forms/HospitalContactForm';
import { AuthContext } from '@lp/core/auth';
import { ContactFormValues } from '@/types';
import { Contact } from '@/api/apis/facilities';
import { useUpdateHospitalContactMutation } from '@/queries/hospital-contacts';
import { useLoaderData } from '@/hooks/useLoaderData';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { getChangedValues } from '@/utils/helpers';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { useWrappedRemoveHospitalContactMutation } from '@/queries/orchestrator';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';

export const HospitalContactEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<Contact>();
  const { mutateAsync } = useUpdateHospitalContactMutation();
  const { uploadFiles } = useUploadFile(FileType.Facility);
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.HospitalCreate);

  const defaultValues = useMemo<ContactFormValues>(
    () => ({
      parentId: data.parent?.id || '',
      parentName: data.parent?.name || '',
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email || '',
      photoPath: data.photoPath ?? undefined,
      primaryPhone: data.primaryPhone,
      secondaryPhone: data.secondaryPhone || '',
    }),
    [data],
  );

  const handleBackToList = () => {
    navigate('/admin/hospitals/contacts');
  };

  const handleSubmit = async (values: ContactFormValues) => {
    try {
      let uploadedFile;

      if (values.photo) {
        uploadedFile = await uploadFiles([values.photo]);
      }

      const updateBody = {
        parentId: values.parentId,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        primaryPhone: values.primaryPhone,
        secondaryPhone: values.secondaryPhone,
        photoPath: uploadedFile ? uploadedFile[0].fileName : values.photoPath,
      };

      const changedValues = getChangedValues<Partial<ContactFormValues>>(updateBody, defaultValues);

      await mutateAsync({
        id: data.id,
        ...changedValues,
      });

      toast.success(`Contact updated successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const { isOpen, isLoading, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useWrappedRemoveHospitalContactMutation,
    successMessage: 'Donor Hospital contact deleted successfully.',
    onDeleteCb: handleBackToList,
  });

  const handleDelete = () => {
    onModalOpen(data.id);
  };

  return (
    <>
      <Form<ContactFormValues> defaultValues={defaultValues} schema={hospitalContactFormSchema} onSubmit={handleSubmit}>
        <div className="flex flex-col gap-9">
          <AdminPageHeader title="Edit Donor Hospital Contact" backToListLink="/admin/hospitals/contacts">
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handleDelete}
              hasEditPermissions={hasCreatePermissions}
              hasDeletePermissions={hasCreatePermissions}
            />
          </AdminPageHeader>
          <HospitalContactForm isPrimary={data.isPrimary} isReadOnly={!hasCreatePermissions} />
        </div>
      </Form>
      <DeleteModal
        title="Delete Donor Hospital Contact?"
        description="Are you sure you want to delete the Donor Hospital Contact?"
        size="lg"
        isOpen={isOpen}
        isLoading={isLoading}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
